import {
  Equipment,
  EquipmentBar,
  EquipmentFixed,
  EquipmentPlates,
  EquipmentStack,
  EquipmentType,
} from '@cf-shared/data-model/equipment';
import {
  EquipmentScanItem,
  EQUIPMENT_MERGE_TOLERANCE_KG,
  ScanSupportedEquipmentType,
} from '@cf-shared/scan-equipment-interface';

const LB_PER_KG = 2.20462262185;

/** User-pickable type keys in the scanner dropdown — mirrors equipment-edit-page templates 1:1.
 *  `dumbbells` and `kettlebells` both collapse onto EquipmentType.Fixed. */
export type ScannerTypeKey = 'barbell' | 'plates' | 'cable-machine' | 'dumbbells' | 'kettlebells';

/**
 * Which equipment type each dropdown key means.
 *
 * This is one of the two tables that between them say everything the scanner knows about an
 * equipment type, so that adding a type is a compiler error rather than a silent gap. This one is
 * keyed by dropdown key and is exhaustive over the keys; `SCANNER_TYPES` below is keyed by type and
 * is exhaustive over the types. Two tables and not one, because the dropdown offers more keys than
 * there are types — `dumbbells` and `kettlebells` are both `Fixed`, a cosmetic split that keeps the
 * list the same as the one on the equipment-edit page — so neither key can carry the other's
 * coverage.
 *
 * The `describe('the scanner type tables')` block in the test file beside this one pins the two
 * against each other, which is the only thing that can stop them disagreeing about what a key means.
 */
export const SCANNER_TYPE_KEYS: Record<ScannerTypeKey, ScanSupportedEquipmentType> = {
  'barbell':       EquipmentType.Bar,
  'plates':        EquipmentType.Plates,
  'cable-machine': EquipmentType.Stack,
  'dumbbells':     EquipmentType.Fixed,
  'kettlebells':   EquipmentType.Fixed,
};

/** Everything the scanner decides per equipment type, written down once per type. */
interface ScannerTypeDescriptor {
  /** The dropdown key to select when the AI (or an existing draft) says this type. Where two keys
   *  share a type, this is the one the dropdown seeds to. */
  dropdownKey: ScannerTypeKey;
  /** True for the types that carry an add-on `plates[]` microloading field beside their primary
   *  list, so their detected and resulting items split into a primary group plus a micro group. */
  takesMicroPlates: boolean;
  /** Which item kind a detected item becomes when the user picks this type. Derived from the AI's
   *  ORIGINAL kind rather than the current one, so toggling types back and forth is reversible. */
  itemKindForType(originalKind: EquipmentScanItem['kind']): EquipmentScanItem['kind'];
  /** The kg values of this type's PRIMARY list — stack increments, dumbbell weights, or plates.
   *  Excludes micro plates, which `scannerMicroPlateWeightsKg` reads instead. */
  primaryWeightsKg(equipment: Equipment): number[];
}

/**
 * One entry per equipment type the scanner supports. Exhaustive over `ScanSupportedEquipmentType`,
 * which is written as an `Exclude<>` of the whole `EquipmentType` enum — so a type added to that
 * enum widens it on its own and becomes a compile error here, with nobody having to remember this
 * table exists. Without that, the page renders with a blank drawer heading and a count of zero.
 *
 * Every `?? []` below matches the guard `applyEquipmentScanResult` already applies to the same
 * field: a legacy stored document may predate `stack`, `fixed` or `plates`, and the apply helper is
 * the only thing that produces the drafts these are read from (#3523).
 */
const SCANNER_TYPES: Record<ScanSupportedEquipmentType, ScannerTypeDescriptor> = {
  [EquipmentType.Bar]: {
    dropdownKey: 'barbell',
    takesMicroPlates: false,
    // Bar is the one type with several item kinds. Restore the AI's own bar/collar split; anything
    // it called something else (a stack increment, a dumbbell weight) becomes a plate.
    itemKindForType: originalKind => (originalKind === 'bar' || originalKind === 'collar') ? originalKind : 'plate',
    primaryWeightsKg: equipment => ((equipment as EquipmentBar).plates ?? []).map(plate => plate.weight_kg),
  },
  [EquipmentType.Plates]: {
    dropdownKey: 'plates',
    takesMicroPlates: false,
    itemKindForType: () => 'plate',
    primaryWeightsKg: equipment => ((equipment as EquipmentPlates).plates ?? []).map(plate => plate.weight_kg),
  },
  [EquipmentType.Stack]: {
    dropdownKey: 'cable-machine',
    takesMicroPlates: true,
    // keep micro plates the AI flagged as `plate`; everything else becomes a stack increment
    itemKindForType: originalKind => originalKind === 'plate' ? 'plate' : 'stack',
    primaryWeightsKg: equipment => ((equipment as EquipmentStack).stack ?? []).map(increment => increment.totalWeight_kg),
  },
  [EquipmentType.Fixed]: {
    dropdownKey: 'dumbbells',
    takesMicroPlates: true,
    // keep micro plates the AI flagged as `plate`; everything else becomes a dumbbell weight
    itemKindForType: originalKind => originalKind === 'plate' ? 'plate' : 'fixed',
    primaryWeightsKg: equipment => ((equipment as EquipmentFixed).fixed ?? []).map(weight => weight.weight_kg),
  },
};

/** True when this is a type the scanner supports. Narrows, so a caller can then switch over the
 *  supported types with no default arm and let the compiler check it covered them all.
 *
 *  `hasOwnProperty` rather than `in`: `in` also finds inherited keys, so a stored document whose
 *  `type` held `'toString'` or `'constructor'` would pass this guard and then blow up on the
 *  descriptor lookup. The switch statements this replaced answered such a value with their default
 *  arm instead of throwing. */
export function isScannerEquipmentType(type: EquipmentType | 'unknown' | undefined): type is ScanSupportedEquipmentType {
  return type !== undefined && type !== 'unknown' && Object.prototype.hasOwnProperty.call(SCANNER_TYPES, type);
}

/** The equipment type a dropdown key means. */
export function scannerTypeForKey(key: ScannerTypeKey): ScanSupportedEquipmentType {
  return SCANNER_TYPE_KEYS[key];
}

/** The dropdown key to seed from a detected or draft type; undefined for a type the scanner does not
 *  support, which leaves the dropdown on its placeholder so the user has to pick one. */
export function scannerKeyForType(type: EquipmentType | 'unknown' | undefined): ScannerTypeKey | undefined {
  return isScannerEquipmentType(type) ? SCANNER_TYPES[type].dropdownKey : undefined;
}

/** True when this type splits its items into a primary list plus add-on microloading plates. */
export function scannerTypeTakesMicroPlates(type: EquipmentType | 'unknown' | undefined): boolean {
  return isScannerEquipmentType(type) && SCANNER_TYPES[type].takesMicroPlates;
}

/** What a detected item's kind becomes under the given type. */
export function scannerItemKindForType(type: ScanSupportedEquipmentType, originalKind: EquipmentScanItem['kind']): EquipmentScanItem['kind'] {
  return SCANNER_TYPES[type].itemKindForType(originalKind);
}

/** The kg values of an equipment's primary list — stack increments, dumbbell weights, or plates.
 *  Empty for a type the scanner does not support. Excludes micro plates. */
export function scannerPrimaryWeightsKg(equipment: Equipment): number[] {
  return isScannerEquipmentType(equipment.type) ? SCANNER_TYPES[equipment.type].primaryWeightsKg(equipment) : [];
}

/** The kg values of an equipment's add-on microloading plates. Empty for the types that do not carry
 *  them — for Bar and Plates the `plates[]` field IS the primary list, not a micro list. */
export function scannerMicroPlateWeightsKg(equipment: Equipment): number[] {
  if (!scannerTypeTakesMicroPlates(equipment.type)) return [];
  return ((equipment as EquipmentStack | EquipmentFixed).plates ?? []).map(plate => plate.weight_kg);
}

/** Build an empty Equipment of the given supported type, ready to receive scan results in auto-detect mode. */
export function freshEquipmentOfType(id: string, type: ScanSupportedEquipmentType): Equipment {
  switch (type) {
    case EquipmentType.Bar:    return { id, name: '', type: EquipmentType.Bar, plates: [] };
    case EquipmentType.Plates: return { id, name: '', type: EquipmentType.Plates, plates: [] };
    case EquipmentType.Stack:  return { id, name: '', type: EquipmentType.Stack, stack: [], plates: [] };
    case EquipmentType.Fixed:  return { id, name: '', type: EquipmentType.Fixed, fixed: [], plates: [] };
  }
}

/** Convert a weight value between kg and lb. Identity when from === to. */
export function convertWeightUnit(value: number, from: 'kg' | 'lb', to: 'kg' | 'lb'): number {
  if (from === to) return value;
  return from === 'kg' ? value * LB_PER_KG : value / LB_PER_KG;
}

export type ApplyMode = 'replace' | 'merge' | 'append';

/**
 * Apply checked detected items to an equipment draft. Pure function — does not mutate `draft`.
 *
 * Only the items in `checkedItems` are considered (unchecked detections are discarded by the
 * caller). Item weights are interpreted in `detectedUnit` and converted to kg before writing
 * to the draft (the data model stores `*_kg` fields).
 *
 * Replace: overwrite the existing list with the checked detected list.
 * Merge:   union of existing + detected, deduped by weight within EQUIPMENT_MERGE_TOLERANCE_KG.
 * Append:  concatenate detected onto existing (duplicates allowed).
 */
export function applyEquipmentScanResult(
  draft: Equipment,
  checkedItems: EquipmentScanItem[],
  mode: ApplyMode,
  detectedUnit: 'kg' | 'lb',
  acceptedName?: string,
): Equipment {
  const detectedKg = (it: EquipmentScanItem) => convertWeightUnit(it.weight, detectedUnit, 'kg');

  // group items by kind
  const stackItems = checkedItems.filter(i => i.kind === 'stack').map(detectedKg);
  const plateItems = checkedItems.filter(i => i.kind === 'plate').map(detectedKg);
  const fixedItems = checkedItems.filter(i => i.kind === 'fixed').map(detectedKg);
  const barItem = checkedItems.find(i => i.kind === 'bar');     // single instance per server collapse
  const collarItem = checkedItems.find(i => i.kind === 'collar');

  const newName = acceptedName && acceptedName !== draft.name ? acceptedName : draft.name;
  // Carry the chosen unit onto the equipment so the parent edit page renders weights in the
  // unit the user picked during the scan. Only seed when the draft doesn't already have one
  // (don't surprise-flip an existing equipment's unit just because the scan was kg/lb).
  const newUnit = draft.weight_unit ?? detectedUnit;

  switch (draft.type) {
    case EquipmentType.Stack: {
      // guard `stack`/`plates` with `?? []`: a legacy stored doc may predate either field, and the
      // sibling equipment-edit page treats both as possibly-undefined (#3523)
      const current = ((draft as EquipmentStack).stack ?? []).map(s => s.totalWeight_kg);
      const merged = mergeWeights(current, stackItems, mode);
      // stack is sorted ascending and deduped by data-model invariant
      const sortedDedup = sortAscDedup(merged);
      // detected `plate` items are microloading add-on plates for this stack — they live in the
      // equipment's `plates[]` field (the edit page's "Micro Plates" section). Keep duplicates (count
      // matters), same as the Plates/Bar handling. (#3523)
      const currentMicro = ((draft as EquipmentStack).plates ?? []).map(p => p.weight_kg);
      const mergedMicro = sortAsc(mergeWeights(currentMicro, plateItems, mode));
      return {
        ...(draft as EquipmentStack),
        name: newName,
        weight_unit: newUnit,
        stack: sortedDedup.map(w => ({ totalWeight_kg: w })),
        plates: mergedMicro.map(w => ({ weight_kg: w })),
      };
    }
    case EquipmentType.Plates: {
      const current = (draft as EquipmentPlates).plates.map(p => p.weight_kg);
      const merged = sortAsc(mergeWeights(current, plateItems, mode));
      return { ...(draft as EquipmentPlates), name: newName, weight_unit: newUnit, plates: merged.map(w => ({ weight_kg: w })) };
    }
    case EquipmentType.Fixed: {
      // guard `fixed`/`plates` with `?? []`: a legacy stored doc may predate either field, matching
      // the sibling equipment-edit page's defensive treatment (#3523)
      const current = ((draft as EquipmentFixed).fixed ?? []).map(f => f.weight_kg);
      const merged = sortAsc(mergeWeights(current, fixedItems, mode));
      // like Stack, a Fixed (dumbbell) config also carries an add-on `plates[]` micro-plate field.
      // Route detected `plate` items there, keeping duplicates. (#3523)
      const currentMicro = ((draft as EquipmentFixed).plates ?? []).map(p => p.weight_kg);
      const mergedMicro = sortAsc(mergeWeights(currentMicro, plateItems, mode));
      return {
        ...(draft as EquipmentFixed),
        name: newName,
        weight_unit: newUnit,
        fixed: merged.map(w => ({ weight_kg: w })),
        plates: mergedMicro.map(w => ({ weight_kg: w })),
      };
    }
    case EquipmentType.Bar: {
      const bar = draft as EquipmentBar;
      const current = bar.plates.map(p => p.weight_kg);
      const merged = sortAsc(mergeWeights(current, plateItems, mode));
      const detectedBarKg = barItem ? convertWeightUnit(barItem.weight, detectedUnit, 'kg') : undefined;
      const detectedCollarKg = collarItem ? convertWeightUnit(collarItem.weight, detectedUnit, 'kg') : undefined;
      return {
        ...bar,
        name: newName,
        weight_unit: newUnit,
        // Replace: detected wins if present; Merge/Append: detected wins only if present (existing kept otherwise)
        bar_kg: detectedBarKg ?? (mode === 'replace' ? undefined : bar.bar_kg),
        collar_kg: detectedCollarKg ?? (mode === 'replace' ? undefined : bar.collar_kg),
        plates: merged.map(w => ({ weight_kg: w })),
      };
    }
    default:
      // unsupported type (Manual / Unknown) — return draft unchanged. Should never reach here in
      // practice because the entry-point button is hidden for these types
      return draft;
  }
}

/** Merge existing + detected weight arrays per the apply-mode rules. */
function mergeWeights(existing: number[], detected: number[], mode: ApplyMode): number[] {
  switch (mode) {
    case 'replace':
      return detected.slice();
    case 'append':
      return [...existing, ...detected];
    case 'merge': {
      // suppress a detected value only when it already exists in the ORIGINAL list (so a re-scan
      // doesn't re-add an already-configured weight). Dedup against `existing`, NOT the growing
      // output — otherwise duplicates WITHIN the detected set collapse, e.g. two scanned 0.625 micro
      // plates would become one. Two checked physical plates are two plates the user wants. (#3523)
      const out = existing.slice();
      for (const d of detected) {
        if (!existing.some(e => Math.abs(e - d) < EQUIPMENT_MERGE_TOLERANCE_KG)) {
          out.push(d);
        }
      }
      return out;
    }
    default: {
      // exhaustive check — `mode` is `never` here; if a new ApplyMode is added the
      // compiler will flag this branch
      const _exhaustive: never = mode;
      void _exhaustive;
      return [];
    }
  }
}

/** Sort ascending, preserving duplicates. Used for plates/fixed where the count matters. */
function sortAsc(values: number[]): number[] {
  return values.slice().sort((a, b) => a - b);
}

/** Stack invariant: ascending order with strictly-increasing weights. */
function sortAscDedup(values: number[]): number[] {
  const sorted = values.slice().sort((a, b) => a - b);
  const out: number[] = [];
  for (const v of sorted) {
    if (out.length === 0 || Math.abs(out[out.length - 1] - v) >= EQUIPMENT_MERGE_TOLERANCE_KG) {
      out.push(v);
    }
  }
  return out;
}
